import { Injectable } from '@angular/core';
import { Order, QueryEntity } from '@datorama/akita';
import { TimeSeriesDataStore, TimeSeriesDataState } from './time-series-data.store';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import {
    eachMonthOfInterval,
    eachQuarterOfInterval,
    eachYearOfInterval,
    format,
    startOfMonth,
    startOfQuarter,
    startOfYear,
    subMonths,
    subQuarters,
    subYears
} from 'date-fns';
import { BaseTimeSeriesData } from './time-series-data.model';
import {
    checkFilter,
    flattenNestedObject,
    QueryFilterNew
} from 'src/app/shared/state/query-filter.functions';
import { get } from 'dot-prop';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class TimeSeriesDataQuery extends QueryEntity<TimeSeriesDataState> {
    constructor(protected store: TimeSeriesDataStore) {
        super(store);
    }

    selectTimeSeriesDataById$(id: string) {
        return this.selectEntity(id);
    }

    selectTimeSeriesByInstrumentId$(instrumentId: number) {
        return this.selectAll({
            sortBy: 'timestamp',
            sortByOrder: Order.ASC,
            filterBy: (entity) => entity.instrumentId === instrumentId
        });
    }

    getTimeSeriesDataByInstrumentId = (instrumentId: number) => {
        return this.getAll({
            sortBy: 'timestamp',
            sortByOrder: Order.ASC,
            filterBy: (entity) => entity.instrumentId === instrumentId
        });
    };

    selectByFilter$<T>(filters?: QueryFilterNew<BaseTimeSeriesData>) {
        return <Observable<Array<BaseTimeSeriesData<T>>>>this.selectAll({
            sortBy: 'timestamp',
            sortByOrder: Order.ASC,
            filterBy: (timeSeriesData) => {
                // ...flatten nested object and filter out null values
                const FILTERS = Object.entries<any>(flattenNestedObject(filters)).filter(
                    (filter) => filter[1] !== null
                );
                if (FILTERS.length > 0) {
                    return !FILTERS.find(([key, criteria]) => {
                        const value = get<string | number>(timeSeriesData, key);
                        return !checkFilter(value, criteria);
                    });
                }
                // No filters provided --> return true (all matching).
                return true;
            }
        });
    }

    /** accumulateTimeSeriesData$ - accumulate time series data by period
     * @param timeSeriesDataIds - ids to accumulate
     * @param period - period to accumulate by
     * @param accumulatorFunction - function used to accumulate values
     * @param returnAsObject - return as object instead of array
     * @returns Observable<Array<T>> - array of accumulated values
     */
    accumulateTimeSeriesData$ = <T>(
        timeSeriesDataIds: Array<string>,
        period: 'yyyy' | 'MMM yy' | 'QQQ yy' = 'yyyy',
        accumulatorFunction?: (timeSeriesData: BaseTimeSeriesData, previous?: T) => T,
        returnAsObject = false
    ) => {
        // select time series data based on ids...
        return this.selectMany([...timeSeriesDataIds]).pipe(
            map((timeSeriesData) =>
                // ...and sort them ascending by date
                timeSeriesData.sort((a, b) => a.timestamp - b.timestamp)
            ),
            map((timeSeriesData) => {
                // return empty array if no timeSeriesData
                if (!timeSeriesData || timeSeriesData.length === 0) {
                    return [];
                }

                // create placeholder for groups/periods
                const GROUPS: { [period: string]: any } = {};

                // loop through timeSeriesData...
                for (let i = 0; i < timeSeriesData.length; i++) {
                    // ...declare timeSeriesData item as LegacyMarketData
                    const TIME_SERIES_DATA = timeSeriesData[i];

                    // ...declare group based on formatted dated (according to chosen period)
                    const GROUP = format(TIME_SERIES_DATA.timestamp, period);
                    // ...group timeSeriesData by group
                    if (!GROUPS[GROUP]) {
                        // ...if group does not exist, create it
                        GROUPS[GROUP] = {
                            // ...pass timeSeriesData item to callback function and use result as value for group
                            ...accumulatorFunction(TIME_SERIES_DATA)
                        };
                    } else {
                        /* ...if group exists, pass timeSeriesData item and previous value for group to
                         callback function and use result as new value for group */

                        GROUPS[GROUP] = {
                            ...accumulatorFunction(TIME_SERIES_DATA, GROUPS[GROUP])
                        };
                    }
                }

                // create placeholder for group range containing all groups between first and last timeSeriesData item
                let GROUP_RANGE: { [period: string]: any };
                const FIRST_VALUE = timeSeriesData[0].timestamp;
                const indexOfLastValue = timeSeriesData.length - 1;
                const LAST_VALUE = timeSeriesData[indexOfLastValue].timestamp;

                let timestampFunction: Function;

                // assign group range based on period
                if (period === 'QQQ yy') {
                    GROUP_RANGE = eachQuarterOfInterval({
                        start: FIRST_VALUE,
                        end: LAST_VALUE
                    });
                    timestampFunction = startOfQuarter;
                } else if (period === 'MMM yy') {
                    GROUP_RANGE = eachMonthOfInterval({
                        start: FIRST_VALUE,
                        end: LAST_VALUE
                    });
                    timestampFunction = startOfMonth;
                } else if (period === 'yyyy') {
                    GROUP_RANGE = eachYearOfInterval({
                        start: FIRST_VALUE,
                        end: LAST_VALUE
                    });
                    timestampFunction = startOfYear;
                }

                // loop through group range...
                const ALL_GROUPS: { [key: string]: T } = GROUP_RANGE.reduce(
                    (groups: { [key: string]: T }, date: number | Date) => {
                        // ...declare group name based on formatted dated (according to chosen period)
                        const group = format(date, period);

                        // ...get previous group name based on formatted dated (according to chosen period)
                        let previous;
                        if (period === 'QQQ yy') {
                            previous = format(subQuarters(date, 1), period);
                        } else if (period === 'MMM yy') {
                            previous = format(subMonths(date, 1), period);
                        } else if (period === 'yyyy') {
                            previous = format(subYears(date, 1), period);
                        }

                        // reassign groups for whole range
                        groups[group] = {
                            ...(GROUPS[group] ||
                                accumulatorFunction(null, groups[previous])),
                            period: group,
                            periodStart: timestampFunction(date).getTime()
                        };
                        return groups;
                    },
                    {}
                );
                return returnAsObject ? ALL_GROUPS : Object.values(ALL_GROUPS);
            })
        );
    };

    /** lastDataEntryByUser$
     * @description date when user last added or modified a time series value during active session
     * @returns Observable<Date>
     */
    get lastDataEntryByUser$() {
        return this.select((state) => state.ui.lastEntryByUser).pipe(
            distinctUntilChanged(),
            shareReplay(1)
        );
    }
}
